import { useEffect, useState } from 'react';
import TopBar from '../components/TopBar';
import { api } from '../api/client';
import { useAuth } from '../stores/auth';

const WMO: Record<number, { icon: string; desc: string }> = {
  0:  { icon: '☀️',  desc: 'Despejado' },
  1:  { icon: '🌤️', desc: 'Mayormente despejado' },
  2:  { icon: '⛅',  desc: 'Parcialmente nublado' },
  3:  { icon: '☁️',  desc: 'Nublado' },
  45: { icon: '🌫️', desc: 'Niebla' },
  48: { icon: '🌫️', desc: 'Niebla' },
  51: { icon: '🌦️', desc: 'Llovizna leve' },
  53: { icon: '🌦️', desc: 'Llovizna' },
  55: { icon: '🌧️', desc: 'Llovizna intensa' },
  61: { icon: '🌧️', desc: 'Lluvia leve' },
  63: { icon: '🌧️', desc: 'Lluvia' },
  65: { icon: '🌧️', desc: 'Lluvia intensa' },
  71: { icon: '❄️',  desc: 'Nieve leve' },
  73: { icon: '❄️',  desc: 'Nieve' },
  75: { icon: '❄️',  desc: 'Nieve intensa' },
  80: { icon: '🌦️', desc: 'Chubascos leves' },
  81: { icon: '🌧️', desc: 'Chubascos' },
  82: { icon: '⛈️',  desc: 'Chubascos fuertes' },
  95: { icon: '⛈️',  desc: 'Tormenta' },
  96: { icon: '⛈️',  desc: 'Tormenta con granizo' },
  99: { icon: '⛈️',  desc: 'Tormenta con granizo fuerte' },
};
function wmo(code: number) {
  return WMO[code] ?? { icon: '🌡️', desc: '–' };
}

const DIAS = ['Dom', 'Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb'];

type Data = {
  current: any;
  daily: {
    time: string[];
    weather_code: number[];
    temperature_2m_max: number[];
    temperature_2m_min: number[];
    precipitation_sum: number[];
    wind_speed_10m_max: number[];
  };
  ciudad: string;
};

export default function Clima() {
  const ciudadano = useAuth((s) => s.ciudadano);
  const mid = ciudadano?.municipio_id ?? 0;

  const [data, setData]       = useState<Data | null>(null);
  const [cargando, setCargando] = useState(true);
  const [err, setErr]         = useState<string | null>(null);

  useEffect(() => {
    if (!mid) return;
    api<Data>(`/municipios?action=clima_7dias&municipio_id=${mid}`, { auth: false })
      .then(setData)
      .catch((e) => setErr(e.message))
      .finally(() => setCargando(false));
  }, [mid]);

  const c = data?.current;
  const d = data?.daily;

  return (
    <>
      <TopBar title="Clima" back />
      <main className="max-w-md mx-auto px-4 py-4 pb-24 space-y-4">

        {cargando && <p className="text-center text-gray-400 pt-10">Cargando…</p>}
        {err && (
          <div className="bg-red-50 border border-red-200 text-red-700 text-sm rounded-xl p-3">{err}</div>
        )}

        {c && data && (
          <>
            {/* Condición actual */}
            <div className="bg-white border border-app rounded-2xl p-5 text-center">
              <p className="text-6xl mb-2 leading-none">{wmo(c.weather_code).icon}</p>
              <p className="text-5xl font-extrabold text-gray-900 tracking-tight">
                {Math.round(c.temperature_2m)}°C
              </p>
              <p className="text-base text-gray-600 mt-1">{wmo(c.weather_code).desc}</p>
              <p className="text-sm text-gray-400 mt-0.5">{data.ciudad}</p>

              <div className="grid grid-cols-4 gap-2 mt-4">
                <StatTile emoji="🌡️" val={`${Math.round(c.apparent_temperature)}°`} label="Sens." />
                <StatTile emoji="💧" val={`${c.relative_humidity_2m}%`}             label="Hum." />
                <StatTile emoji="💨" val={`${Math.round(c.wind_speed_10m)}km`}      label="Viento" />
                <StatTile emoji="🌂" val={`${c.precipitation}mm`}                   label="Lluvia" />
              </div>
            </div>

            {/* Pronóstico 7 días */}
            {d && (
              <div className="bg-white border border-app rounded-2xl overflow-hidden">
                <p className="text-xs font-bold text-gray-400 uppercase tracking-widest px-4 pt-3 pb-2">
                  Pronóstico 7 días
                </p>
                {d.time.map((fecha, i) => {
                  const w    = wmo(d.weather_code[i]);
                  const dia  = new Date(fecha + 'T12:00:00');
                  const hoy  = i === 0;
                  return (
                    <div
                      key={fecha}
                      className={`flex items-center px-4 py-3 gap-3 ${i > 0 ? 'border-t border-gray-100' : ''} ${hoy ? 'bg-brand-50' : ''}`}
                    >
                      <span className="text-sm font-semibold text-gray-500 w-9 flex-shrink-0">
                        {hoy ? 'Hoy' : DIAS[dia.getDay()]}
                      </span>
                      <span className="text-xl leading-none flex-shrink-0">{w.icon}</span>
                      <span className="text-xs text-gray-500 flex-1 leading-snug">{w.desc}</span>
                      {d.precipitation_sum[i] > 0 && (
                        <span className="text-xs text-blue-500 flex-shrink-0">
                          {d.precipitation_sum[i].toFixed(1)}mm
                        </span>
                      )}
                      <span className="text-sm text-gray-400 flex-shrink-0 font-medium">
                        {Math.round(d.temperature_2m_min[i])}°
                      </span>
                      <span className="text-sm font-bold text-gray-900 flex-shrink-0 w-10 text-right">
                        {Math.round(d.temperature_2m_max[i])}°
                      </span>
                    </div>
                  );
                })}
              </div>
            )}

            <p className="text-center text-xs text-gray-300">Fuente: Open-Meteo · actualizado al abrir</p>
          </>
        )}
      </main>
    </>
  );
}

function StatTile({ emoji, val, label }: { emoji: string; val: string; label: string }) {
  return (
    <div className="bg-gray-50 rounded-xl py-2 px-1 flex flex-col items-center gap-0.5">
      <span className="text-base leading-none">{emoji}</span>
      <span className="font-bold text-sm text-gray-900">{val}</span>
      <span className="text-xs text-gray-400">{label}</span>
    </div>
  );
}
